export type Lang = "en" | "fr" | "es";

export const translations = {
  en: {
    nav: {
      links: [
        { label: "Home", href: "#" },
        { label: "Pillars", href: "#poles" },
        { label: "Projects", href: "#projects" },
        { label: "Territory", href: "#territoire" },
        { label: "Catalogue", href: "#collection" },
        { label: "Process", href: "#process" },
        { label: "Contact", href: "#contact" },
      ],
      cta: "Start Your Project",
    },
    hero: {
      title: "We design living spaces through nature.",
      subtitle: "Premium botanical scenography, plant rental, and immersive set design for brands, events, and creators.",
      cta1: "Start My Project",
      cta2: "Explore the Catalogue",
      animated: {
        lead: "Spaces made",
        before: "to feel",
        after: "",
        nowrap: true,
        words: ["alive.", "immersive.", "singular.", "memorable."],
      },
    },
    philosophy: {
      label: "A Manifesto",
      title: "We don't place plants.",
      highlight: "We compose atmospheres where nature becomes architecture.",
      body: "VisioNature is a botanical design studio. We work with living matter, light and space the way an architect works with stone — to give places an identity, a tension, a memory. Every commission is a singular environment, conceived from intention rather than catalogue.",
      tenets: [
        { k: "We transform", v: "spaces, not surfaces." },
        { k: "We compose", v: "atmospheres, not arrangements." },
        { k: "We design", v: "experiences, not decoration." },
        { k: "We give", v: "identity to places." },
      ],
      caption: "Material study — nº 04",
    },
    poles: {
      label: "Our Three Pillars",
      title: "One studio, three living disciplines.",
      intro: "Three complementary approaches to imagine, deploy, and transform spaces through nature.",
      keywords: ["Design", "Deploy", "Transform"],
      items: [
        {
          title: "Botanical Scenography",
          desc: "Weddings, private events, brands, fairs and immersive experiences.",
          cta: "Discover the approach",
          subtitle: "Conceive · Compose · Stage",
          ctaLink: "Give form to an intention",
          steps: ["Scenographic study", "Floral composition", "On-site installation"],
          noteLabel: "Studio note",
          noteQuote: "A transition drawn by the living.",
        },
        {
          title: "Plant Rental",
          desc: "Premium botanical solutions for events, hotels, restaurants and professional spaces.",
          cta: "Browse the catalogue",
          subtitle: "Deploy · Install · Maintain",
          ctaLink: "Compose a living set",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
        {
          title: "Tailored Vegetal Design",
          desc: "Visual concepts, artistic installations and botanical creative direction.",
          cta: "See selected projects",
          subtitle: "Transform · Integrate · Elevate",
          ctaLink: "Reveal a space's potential",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
      ],
    },
    universes: {
      label: "Univers — VIII Atmospheres",
      title: "Every project begins with an atmosphere.",
      intro: "Before a plant, a structure, or a colour — there is a feeling. A way of inhabiting a space. Each Visionature commission is built around one of these worlds: a curated atmosphere from which materials, light and botanical composition are drawn.",
      cue: "Scroll through the atmospheres",
      items: [
        { name: "Riviera Minimal", line: "Whitewashed stone, raw linen and a single olive branch. Restraint as a luxury.", keywords: ["stone", "linen", "olive", "silence"] },
        { name: "Mediterranean Garden", line: "Sun-bleached terracotta, wild herbs and the architecture of cypress.", keywords: ["terracotta", "herbs", "cypress", "afternoon light"] },
        { name: "Botanical Dinner", line: "Long tables under suspended foliage. Candlelight, smoke and quiet abundance.", keywords: ["foliage", "candlelight", "intimate", "supper"] },
        { name: "Luxury Hospitality", line: "Lobbies that breathe. Living sculpture for hotels, spas and private residences.", keywords: ["hospitality", "permanent", "sculptural", "presence"] },
        { name: "Architectural Green", line: "Plants treated as load-bearing matter. Verticality, geometry, restraint.", keywords: ["geometry", "vertical", "structure", "discipline"] },
        { name: "French Riviera Event", line: "Côte d'Azur theatre — pampas, pampered roses and the scent of pine at dusk.", keywords: ["pampas", "rose", "pine", "Côte d'Azur"] },
        { name: "Luxury Wedding", line: "A ceremony framed in living architecture. Ivory upon ivory, and one true green.", keywords: ["ceremony", "ivory", "ritual", "couture"] },
        { name: "Contemporary Botanical", line: "Gallery posture. Plants as installation, the room as a canvas.", keywords: ["gallery", "installation", "minimal", "art"] },
      ],
    },
    collection: {
      label: "Collection — IV Families",
      title: "Collection Visionature.",
      intro: "Over 120 botanical references, floral compositions, containers and decorative elements available to imagine tailor-made projects.",
      statNumber: "120+",
      statLabel: "References available",
      families: [
        { num: "01", name: "Plants", line: "Mediterranean plants, indoor specimens and exceptional subjects for all types of spaces.", href: "/catalogue?category=large-plants" },
        { num: "02", name: "Floral Compositions", line: "Floral creations designed for events, receptions and immersive experiences.", href: "/catalogue?category=floral-compositions" },
        { num: "03", name: "Planters & Containers", line: "Decorative containers selected to enhance every environment.", href: "/catalogue?category=planters" },
        { num: "04", name: "Decorative Structures", line: "Architectural elements and botanical supports for creating remarkable installations.", href: "/catalogue?category=structures" },
      ],
      cta: "Explore",
      ctaMain: "Explore the catalogue",
    },
    services: {
      label: "What We Do",
      title: "End-to-end botanical design",
      items: [
        { title: "Botanical Rental", desc: "Curated premium plant collections for short-term events, long-term installations, and editorial productions." },
        { title: "Custom Set Design", desc: "Tailor-made scenography for brand activations, photoshoots, and immersive experiences." },
        { title: "Event & Installation", desc: "Full-service delivery, setup, and on-site execution with meticulous attention to detail." },
        { title: "Creative Direction", desc: "Artistic vision, styling, and spatial composition to elevate your project beyond expectation." },
      ],
    },
    projects: {
      label: "Selected Work",
      title: "Projects that breathe",
      items: [
        { title: "UTOPAI Studios", cat: "Botanical Stand Design" },
        { title: "MADD Entertainment", cat: "Floral Stand Design" },
        { title: "Kérastase · L'Oréal", cat: "Floral & Botanical Stand Design" },
        { title: "Monaco Red Cross Gala", cat: "Event Floral Scenography" },
        { title: "Netflix Fan Club", cat: "Immersive Floral & Botanical Scenography" },
        { title: "INTERPARFUMS\u00a0", cat: "Botanical Set Design" },
        { title: "Sea Breeze", cat: "Corporate Space" },
        { title: "Lions Terrace", cat: "Terrace Botanical Scenography" },
        { title: "DIOR", cat: "Botanical Set Design" },
      ],
    },
    process: {
      label: "The Visionature Journey",
      title: "From inspiration to installation",
      intro: "Four movements, one continuous gesture — a guided passage from the first spark of an idea to the final breath of a finished setting.",
      steps: [
        { num: "01", chapter: "Inspiration", title: "Explore", desc: "Discover atmospheres, collections and possibilities. Every project begins with a slow conversation about intent, mood and place." },
        { num: "02", chapter: "Composition", title: "Build", desc: "Select plants, floral compositions, structures and decorative pieces — assembling not a list of products, but a coherent atmosphere." },
        { num: "03", chapter: "Direction", title: "Design", desc: "Creative direction, scenography and refinement. Ideas are translated into a precise, executable vision held to the highest standard." },
        { num: "04", chapter: "Execution", title: "Install", desc: "Production, logistics, installation and final staging. A seamless arrival, calibrated on site until every detail breathes correctly." },
      ],
    },
    why: {
      label: "Why Visionature",
      title: "We do not install plants.",
      titleAccent: "We design environments that become experiences.",
      body: "Visionature is a botanical design studio — a scenography partner for places that refuse the ordinary. Every commission begins as a conversation about atmosphere, and ends as a setting that holds memory.",
      stats: [
        { value: "120+", label: "Curated references" },
        { value: "100%", label: "Bespoke creations" },
        { value: "FR · EU", label: "On-site execution" },
      ],
      points: [
        { num: "01", title: "Unique by design", desc: "Every project is composed from scratch — no templates, no repetition. Each environment responds to a singular place, audience and intention." },
        { num: "02", title: "A curated collection", desc: "More than 120 carefully selected references — plants, floral creations, containers and structures — assembled to compose coherent, considered atmospheres." },
        { num: "03", title: "Complete mastery", desc: "From concept to installation: creative direction, production, logistics and execution. One team, one vision, held end to end." },
        { num: "04", title: "Designed to leave an impression", desc: "Made to be lived in, photographed, shared, remembered. The goal is never decoration. The goal is presence." },
      ],
    },
    map: {
      legend: { installation: "Installation", client: "Client", hub: "Hub" },
      title: "Where We Operate",
      stats: [
        { number: "+1200", title: "Projects Completed", description: "Botanical installations delivered across Europe and the Middle East." },
        { number: "+30", title: "Countries Covered", description: "A growing network of premium botanical experiences worldwide." },
        { number: "+95", title: "Clients Served", description: "Luxury brands, events, and private commissions trust our craft." },
      ],
      info: [
        { title: "International Reach", description: "From the French Riviera to Dubai, we bring living scenography to the world's most prestigious venues." },
        { title: "Mobile Installations", description: "Our team travels with full production capabilities — delivery, setup, and styling on-site anywhere." },
        { title: "Custom Projects", description: "Every project is designed from scratch. No templates, no shortcuts — only bespoke botanical art." },
      ],
    },
    contact: {
      label: "Get In Touch",
      title: "Let's create something extraordinary",
      subtitle: "Tell us about your vision. We'll craft a bespoke botanical experience.",
      thankYou: "Thank you.",
      thankYouSub: "Your project request has been received. Our team will review your inquiry and get back to you shortly.",
      step1Title: "About You",
      step2Title: "Your Project",
      step3Title: "Final Details",
      next: "Continue",
      back: "Back",
      cta: "Start Your Project",
      fullName: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      company: "Company Name",
      clientType: "Type of Client",
      clientTypes: [
        { value: "brand", label: "Brand / Company" },
        { value: "agency", label: "Agency" },
        { value: "creator", label: "Content Creator / Influencer" },
        { value: "private", label: "Private Client (Wedding / Event)" },
        { value: "other", label: "Other" },
      ],
      projectType: "Type of Project",
      projectTypes: [
        { value: "event", label: "Event (Brand / Private)" },
        { value: "photoshoot", label: "Photoshoot / Content Creation" },
        { value: "activation", label: "Brand Activation" },
        { value: "installation", label: "Installation / Decoration" },
        { value: "other", label: "Other" },
      ],
      location: "Project Location",
      locationPlaceholder: "City, Country",
      setting: "Setting",
      indoor: "Indoor",
      outdoor: "Outdoor",
      both: "Both",
      scale: "Estimated Scale / Guests",
      scalePlaceholder: "e.g. 150 guests",
      dates: "Project Dates",
      datesPlaceholder: "Select date range",
      startDate: "Start date",
      endDate: "End date",
      multiEvents: "This project includes multiple event dates",
      additionalDates: "Additional Dates",
      additionalDatesPlaceholder: "Please list any other dates or event periods relevant to your project.",
      addRange: "+ Add another date range",
      removeRange: "Remove",
      budgetRange: "Estimated Budget",
      budgets: [
        { value: "1-5k", label: "1 000€ – 5 000€" },
        { value: "5-10k", label: "5 000€ – 10 000€" },
        { value: "10-20k", label: "10 000€ – 20 000€" },
        { value: "20-35k", label: "20 000€ – 35 000€" },
        { value: "35-50k", label: "35 000€ – 50 000€" },
        { value: "50k+", label: "50 000€+" },
        { value: "tbd", label: "To be defined" },
      ],
      message: "Project Description",
      messagePlaceholder: "Tell us about your vision, atmosphere, references, and expectations.",
      stepOf: "of",
    },
    footer: {
      description: "Premium botanical scenography studio. We design living spaces through nature.",
      navigation: "Navigation",
      contactLabel: "Contact",
      rights: "All rights reserved.",
    },
  },

  fr: {
    nav: {
      links: [
        { label: "Accueil", href: "#" },
        { label: "Pôles", href: "#poles" },
        { label: "Projets", href: "#projects" },
        { label: "Territoire", href: "#territoire" },
        { label: "Catalogue", href: "#collection" },
        { label: "Processus", href: "#process" },
        { label: "Contact", href: "#contact" },
      ],
      cta: "Lancer votre projet",
    },
    hero: {
      title: "Nous concevons des espaces vivants à travers la nature.",
      subtitle: "Scénographie botanique haut de gamme, location de plantes et création d'univers immersifs pour les marques, événements et créateurs.",
      cta1: "Créer mon projet",
      cta2: "Explorer le catalogue",
      animated: {
        lead: "Nous concevons",
        before: "des espaces",
        after: "",
        words: ["vivants.", "immersifs.", "singuliers.", "mémorables."],
      },
    },
    philosophy: {
      label: "Manifeste",
      title: "Nous ne posons pas des plantes.",
      highlight: "Nous composons des atmosphères où la nature devient architecture.",
      body: "VisioNature est un studio de design botanique. Nous travaillons la matière vivante, la lumière et l'espace comme un architecte travaille la pierre — pour donner aux lieux une identité, une tension, une mémoire. Chaque commande devient un environnement singulier, pensé depuis l'intention, jamais depuis le catalogue.",
      tenets: [
        { k: "Nous transformons", v: "des espaces, pas des surfaces." },
        { k: "Nous composons", v: "des atmosphères, pas des bouquets." },
        { k: "Nous concevons", v: "des expériences, pas de la décoration." },
        { k: "Nous donnons", v: "une identité aux lieux." },
      ],
      caption: "Étude de matière — nº 04",
    },
    poles: {
      label: "Nos trois pôles",
      title: "Un studio, trois disciplines vivantes.",
      intro: "Trois approches complémentaires pour imaginer, déployer et transformer les espaces grâce au végétal.",
      keywords: ["Concevoir", "Déployer", "Transformer"],
      items: [
        {
          title: "Scénographie végétale",
          desc: "Mariages, événements privés, marques, salons et expériences immersives.",
          cta: "Découvrir l'approche",
          subtitle: "Concevoir · Composer · Mettre en scène",
          ctaLink: "Donner forme à une intention",
          steps: ["Étude scénographique", "Composition florale", "Installation sur site"],
          noteLabel: "Note de l'atelier",
          noteQuote: "Une transition dessinée par le végétal.",
        },
        {
          title: "Location de plantes",
          desc: "Solutions végétales premium pour événements, hôtels, restaurants et lieux professionnels.",
          cta: "Explorer le catalogue",
          subtitle: "Déployer · Installer · Entretenir",
          ctaLink: "Composer un décor vivant",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
        {
          title: "Végétalisation sur mesure",
          desc: "Concepts visuels, installations artistiques et direction artistique végétale.",
          cta: "Voir les projets",
          subtitle: "Transformer · Intégrer · Valoriser",
          ctaLink: "Révéler le potentiel d'un espace",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
      ],
    },
    universes: {
      label: "Univers — VIII Atmosphères",
      title: "Chaque projet commence par une atmosphère.",
      intro: "Avant une plante, une structure ou une couleur — il y a un sentiment. Une manière d'habiter un lieu. Chaque commande Visionature naît d'un de ces mondes : une atmosphère choisie, à partir de laquelle se composent la matière, la lumière et le végétal.",
      cue: "Faire défiler les atmosphères",
      items: [
        { name: "Riviera Minimal", line: "Pierre blanchie, lin brut et une seule branche d'olivier. La retenue comme luxe.", keywords: ["pierre", "lin", "olivier", "silence"] },
        { name: "Jardin Méditerranéen", line: "Terre cuite patinée par le soleil, herbes sauvages et architecture du cyprès.", keywords: ["terre cuite", "herbes", "cyprès", "lumière du sud"] },
        { name: "Dîner Botanique", line: "Tables longues sous feuillages suspendus. Bougies, fumée et abondance silencieuse.", keywords: ["feuillage", "bougies", "intime", "souper"] },
        { name: "Hospitalité de Luxe", line: "Des halls qui respirent. Sculpture vivante pour hôtels, spas et résidences privées.", keywords: ["hospitalité", "pérenne", "sculptural", "présence"] },
        { name: "Architectural Green", line: "Le végétal traité comme matière porteuse. Verticalité, géométrie, retenue.", keywords: ["géométrie", "vertical", "structure", "discipline"] },
        { name: "Événement Côte d'Azur", line: "Théâtre azuréen — pampas, roses choyées et parfum de pin au crépuscule.", keywords: ["pampas", "rose", "pin", "Côte d'Azur"] },
        { name: "Mariage d'Exception", line: "Une cérémonie cadrée par une architecture vivante. Ivoire sur ivoire, et un seul vrai vert.", keywords: ["cérémonie", "ivoire", "rituel", "couture"] },
        { name: "Botanique Contemporain", line: "Posture de galerie. Le végétal comme installation, la pièce comme toile.", keywords: ["galerie", "installation", "minimal", "art"] },
      ],
    },
    collection: {
      label: "Collection — IV Familles",
      title: "Collection Visionature.",
      intro: "Plus de 120 références de végétaux, compositions florales, contenants et éléments décoratifs disponibles pour imaginer des projets sur mesure.",
      statNumber: "120+",
      statLabel: "Références disponibles",
      families: [
        { num: "01", name: "Végétaux", line: "Plantes méditerranéennes, végétaux d'intérieur et sujets d'exception pour tous types d'espaces.", href: "/catalogue?category=large-plants" },
        { num: "02", name: "Compositions florales", line: "Créations florales conçues pour les événements, réceptions et expériences immersives.", href: "/catalogue?category=floral-compositions" },
        { num: "03", name: "Bacs & Jardinières", line: "Contenants décoratifs sélectionnés pour valoriser chaque environnement.", href: "/catalogue?category=planters" },
        { num: "04", name: "Structures décoratives", line: "Éléments architecturaux et supports végétaux permettant de créer des installations remarquables.", href: "/catalogue?category=structures" },
      ],
      cta: "Explorer",
      ctaMain: "Explorer le catalogue",
    },
    services: {
      label: "Nos savoir-faire",
      title: "Un design botanique de bout en bout",
      items: [
        { title: "Location botanique", desc: "Collections végétales haut de gamme pour événements, installations longue durée et productions éditoriales." },
        { title: "Scénographie sur mesure", desc: "Créations scénographiques uniques pour activations de marque, shootings photo et expériences immersives." },
        { title: "Événement & Installation", desc: "Livraison, montage et exécution sur site avec une attention méticuleuse portée à chaque détail." },
        { title: "Direction artistique", desc: "Vision artistique, stylisme et composition spatiale pour sublimer votre projet au-delà des attentes." },
      ],
    },
    projects: {
      label: "Réalisations",
      title: "Des projets qui respirent",
      items: [
        { title: "UTOPAI Studios", cat: "Végétalisation de stand" },
        { title: "MADD Entertainment", cat: "Compositions florales de stand" },
        { title: "Kérastase · L'Oréal", cat: "Végétalisation florale de stand" },
        { title: "Gala de la Croix-Rouge monégasque", cat: "Scénographie florale événementielle" },
        { title: "Netflix Fan Club", cat: "Scénographie florale & végétale immersive" },
        { title: "INTERPARFUMS\u00a0", cat: "Scénographie végétale" },
        { title: "Sea Breeze", cat: "Espace corporate" },
        { title: "Lions Terrace", cat: "Scénographie végétale de terrasse" },
        { title: "DIOR", cat: "Scénographie végétale" },
      ],
    },
    process: {
      label: "Le parcours Visionature",
      title: "De l'inspiration à l'installation",
      intro: "Quatre mouvements, un même geste continu — un cheminement accompagné, de la première étincelle d'une idée au souffle final d'un décor achevé.",
      steps: [
        { num: "01", chapter: "Inspiration", title: "Explorer", desc: "Découvrir les atmosphères, les collections et les possibles. Chaque projet commence par un échange lent autour de l'intention, du lieu et de l'émotion recherchée." },
        { num: "02", chapter: "Composition", title: "Construire", desc: "Sélectionner végétaux, compositions florales, structures et éléments décoratifs — assembler non pas une liste de produits, mais une atmosphère cohérente." },
        { num: "03", chapter: "Direction", title: "Concevoir", desc: "Direction artistique, scénographie et affinage. Les idées deviennent une vision précise, exécutable, tenue au plus haut niveau d'exigence." },
        { num: "04", chapter: "Exécution", title: "Installer", desc: "Production, logistique, installation et mise en scène finale. Une arrivée maîtrisée, calibrée sur site jusqu'à ce que chaque détail respire juste." },
      ],
    },
    why: {
      label: "Pourquoi Visionature",
      title: "Nous n'installons pas des plantes.",
      titleAccent: "Nous concevons des environnements qui deviennent des expériences.",
      body: "Visionature est un studio de design botanique — un partenaire de scénographie pour les lieux qui refusent l'ordinaire. Chaque commande commence par une conversation sur l'atmosphère, et s'achève en un décor qui retient la mémoire.",
      stats: [
        { value: "120+", label: "Références sélectionnées" },
        { value: "100%", label: "Créations sur mesure" },
        { value: "FR · EU", label: "Exécution sur site" },
      ],
      points: [
        { num: "01", title: "Unique par essence", desc: "Chaque projet est composé de zéro — sans modèle, sans répétition. Chaque environnement répond à un lieu, un public et une intention singuliers." },
        { num: "02", title: "Une collection curatée", desc: "Plus de 120 références soigneusement sélectionnées — végétaux, créations florales, contenants et structures — assemblées pour composer des atmosphères cohérentes et habitées." },
        { num: "03", title: "Maîtrise complète", desc: "Du concept à l'installation : direction artistique, production, logistique et exécution. Une équipe, une vision, tenue de bout en bout." },
        { num: "04", title: "Pensé pour marquer", desc: "Conçu pour être vécu, photographié, partagé, retenu. L'objectif n'est jamais la décoration. L'objectif est la présence." },
      ],
    },
    map: {
      legend: { installation: "Installation", client: "Client", hub: "Siège" },
      title: "Où nous intervenons",
      stats: [
        { number: "+1200", title: "Projets réalisés", description: "Installations botaniques livrées à travers l'Europe et le Moyen-Orient." },
        { number: "+30", title: "Pays couverts", description: "Un réseau croissant d'expériences botaniques haut de gamme dans le monde." },
        { number: "+95", title: "Clients accompagnés", description: "Marques de luxe, événements et commandes privées nous font confiance." },
      ],
      info: [
        { title: "Rayonnement international", description: "De la Côte d'Azur à Dubaï, nous apportons la scénographie vivante aux lieux les plus prestigieux." },
        { title: "Installations mobiles", description: "Notre équipe se déplace avec toutes les capacités de production — livraison, montage et stylisme sur place." },
        { title: "Projets sur mesure", description: "Chaque projet est conçu de zéro. Pas de modèles, pas de raccourcis — uniquement de l'art botanique sur mesure." },
      ],
    },
    contact: {
      label: "Nous contacter",
      title: "Créons ensemble quelque chose d'extraordinaire",
      subtitle: "Parlez-nous de votre vision. Nous créerons une expérience botanique sur mesure.",
      thankYou: "Merci.",
      thankYouSub: "Votre demande de projet a bien été reçue. Notre équipe examinera votre demande et reviendra vers vous rapidement.",
      step1Title: "À propos de vous",
      step2Title: "Votre projet",
      step3Title: "Derniers détails",
      next: "Continuer",
      back: "Retour",
      cta: "Lancer votre projet",
      fullName: "Nom complet",
      email: "Adresse e-mail",
      phone: "Téléphone",
      company: "Nom de l'entreprise",
      clientType: "Type de client",
      clientTypes: [
        { value: "brand", label: "Marque / Entreprise" },
        { value: "agency", label: "Agence" },
        { value: "creator", label: "Créateur de contenu / Influenceur" },
        { value: "private", label: "Client privé (Mariage / Événement)" },
        { value: "other", label: "Autre" },
      ],
      projectType: "Type de projet",
      projectTypes: [
        { value: "event", label: "Événement (Marque / Privé)" },
        { value: "photoshoot", label: "Shooting / Création de contenu" },
        { value: "activation", label: "Activation de marque" },
        { value: "installation", label: "Installation / Décoration" },
        { value: "other", label: "Autre" },
      ],
      location: "Lieu du projet",
      locationPlaceholder: "Ville, Pays",
      setting: "Environnement",
      indoor: "Intérieur",
      outdoor: "Extérieur",
      both: "Les deux",
      scale: "Nombre estimé d'invités",
      scalePlaceholder: "ex. 150 invités",
      dates: "Dates du projet",
      datesPlaceholder: "Sélectionner les dates",
      startDate: "Date de début",
      endDate: "Date de fin",
      multiEvents: "Ce projet comprend plusieurs dates d'événements",
      additionalDates: "Dates supplémentaires",
      additionalDatesPlaceholder: "Veuillez lister les autres dates ou périodes pertinentes pour votre projet.",
      addRange: "+ Ajouter une période",
      removeRange: "Supprimer",
      budgetRange: "Budget estimé",
      budgets: [
        { value: "1-5k", label: "1 000€ – 5 000€" },
        { value: "5-10k", label: "5 000€ – 10 000€" },
        { value: "10-20k", label: "10 000€ – 20 000€" },
        { value: "20-35k", label: "20 000€ – 35 000€" },
        { value: "35-50k", label: "35 000€ – 50 000€" },
        { value: "50k+", label: "50 000€+" },
        { value: "tbd", label: "À définir" },
      ],
      message: "Description du projet",
      messagePlaceholder: "Parlez-nous de votre vision, de l'atmosphère souhaitée, de vos références et de vos attentes.",
      stepOf: "sur",
    },
    footer: {
      description: "Studio de scénographie botanique haut de gamme. Nous concevons des espaces vivants à travers la nature.",
      navigation: "Navigation",
      contactLabel: "Contact",
      rights: "Tous droits réservés.",
    },
  },

  es: {
    nav: {
      links: [
        { label: "Inicio", href: "#" },
        { label: "Áreas", href: "#poles" },
        { label: "Proyectos", href: "#projects" },
        { label: "Territorio", href: "#territoire" },
        { label: "Catálogo", href: "#collection" },
        { label: "Proceso", href: "#process" },
        { label: "Contacto", href: "#contact" },
      ],
      cta: "Iniciar su proyecto",
    },
    hero: {
      title: "Diseñamos espacios vivos a través de la naturaleza.",
      subtitle: "Escenografía botánica de alta gama, alquiler de plantas y creación de universos inmersivos para marcas, eventos y creadores.",
      cta1: "Crear mi proyecto",
      cta2: "Explorar el catálogo",
      animated: {
        lead: "Diseñamos",
        before: "espacios",
        after: "",
        words: ["vivos.", "inmersivos.", "singulares.", "memorables."],
      },
    },
    philosophy: {
      label: "Manifiesto",
      title: "No colocamos plantas.",
      highlight: "Componemos atmósferas donde la naturaleza se vuelve arquitectura.",
      body: "VisioNature es un estudio de diseño botánico. Trabajamos la materia viva, la luz y el espacio como un arquitecto trabaja la piedra: para dar a los lugares una identidad, una tensión, una memoria. Cada encargo se convierte en un entorno singular, pensado desde la intención y nunca desde el catálogo.",
      tenets: [
        { k: "Transformamos", v: "espacios, no superficies." },
        { k: "Componemos", v: "atmósferas, no ramos." },
        { k: "Diseñamos", v: "experiencias, no decoración." },
        { k: "Damos", v: "identidad a los lugares." },
      ],
      caption: "Estudio de materia — nº 04",
    },
    poles: {
      label: "Nuestras tres áreas",
      title: "Un estudio, tres disciplinas vivas.",
      intro: "Tres enfoques complementarios para imaginar, desplegar y transformar los espacios a través de lo vegetal.",
      keywords: ["Concebir", "Desplegar", "Transformar"],
      items: [
        {
          title: "Escenografía vegetal",
          desc: "Bodas, eventos privados, marcas, ferias y experiencias inmersivas.",
          cta: "Descubrir el enfoque",
          subtitle: "Concebir · Componer · Escenificar",
          ctaLink: "Dar forma a una intención",
          steps: ["Estudio escenográfico", "Composición floral", "Instalación in situ"],
          noteLabel: "Nota del estudio",
          noteQuote: "Una transición dibujada por lo vivo.",
        },
        {
          title: "Alquiler de plantas",
          desc: "Soluciones vegetales premium para eventos, hoteles, restaurantes y espacios profesionales.",
          cta: "Explorar el catálogo",
          subtitle: "Desplegar · Instalar · Mantener",
          ctaLink: "Componer un decorado vivo",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
        {
          title: "Diseño vegetal a medida",
          desc: "Conceptos visuales, instalaciones artísticas y dirección artística vegetal.",
          cta: "Ver los proyectos",
          subtitle: "Transformar · Integrar · Realzar",
          ctaLink: "Revelar el potencial de un espacio",
          steps: [],
          noteLabel: "",
          noteQuote: "",
        },
      ],
    },
    universes: {
      label: "Universos — VIII Atmósferas",
      title: "Cada proyecto comienza con una atmósfera.",
      intro: "Antes de una planta, una estructura o un color, hay una sensación. Una manera de habitar un lugar. Cada encargo de Visionature nace de uno de estos mundos: una atmósfera elegida a partir de la cual se componen la materia, la luz y lo vegetal.",
      cue: "Desplazarse por las atmósferas",
      items: [
        { name: "Riviera Minimal", line: "Piedra encalada, lino crudo y una sola rama de olivo. La contención como lujo.", keywords: ["piedra", "lino", "olivo", "silencio"] },
        { name: "Jardín Mediterráneo", line: "Terracota curtida por el sol, hierbas silvestres y la arquitectura del ciprés.", keywords: ["terracota", "hierbas", "ciprés", "luz del sur"] },
        { name: "Cena Botánica", line: "Mesas largas bajo follajes suspendidos. Velas, humo y abundancia silenciosa.", keywords: ["follaje", "velas", "íntimo", "cena"] },
        { name: "Hospitalidad de Lujo", line: "Vestíbulos que respiran. Escultura viva para hoteles, spas y residencias privadas.", keywords: ["hospitalidad", "permanente", "escultural", "presencia"] },
        { name: "Architectural Green", line: "Lo vegetal tratado como materia portante. Verticalidad, geometría, contención.", keywords: ["geometría", "vertical", "estructura", "disciplina"] },
        { name: "Evento Costa Azul", line: "Teatro mediterráneo: pampas, rosas mimadas y aroma a pino al anochecer.", keywords: ["pampas", "rosa", "pino", "Côte d'Azur"] },
        { name: "Boda de Excepción", line: "Una ceremonia enmarcada por una arquitectura viva. Marfil sobre marfil, y un solo verde verdadero.", keywords: ["ceremonia", "marfil", "ritual", "couture"] },
        { name: "Botánico Contemporáneo", line: "Actitud de galería. Lo vegetal como instalación, la sala como lienzo.", keywords: ["galería", "instalación", "minimal", "arte"] },
      ],
    },
    collection: {
      label: "Colección — IV Familias",
      title: "Colección Visionature.",
      intro: "Más de 120 referencias de plantas, composiciones florales, contenedores y elementos decorativos disponibles para imaginar proyectos a medida.",
      statNumber: "120+",
      statLabel: "Referencias disponibles",
      families: [
        { num: "01", name: "Plantas", line: "Plantas mediterráneas, especies de interior y ejemplares excepcionales para todo tipo de espacios.", href: "/catalogue?category=large-plants" },
        { num: "02", name: "Composiciones florales", line: "Creaciones florales concebidas para eventos, recepciones y experiencias inmersivas.", href: "/catalogue?category=floral-compositions" },
        { num: "03", name: "Maceteros y contenedores", line: "Contenedores decorativos seleccionados para realzar cada entorno.", href: "/catalogue?category=planters" },
        { num: "04", name: "Estructuras decorativas", line: "Elementos arquitectónicos y soportes vegetales para crear instalaciones memorables.", href: "/catalogue?category=structures" },
      ],
      cta: "Explorar",
      ctaMain: "Explorar el catálogo",
    },
    services: {
      label: "Nuestro saber hacer",
      title: "Diseño botánico de principio a fin",
      items: [
        { title: "Alquiler botánico", desc: "Colecciones vegetales de alta gama para eventos, instalaciones de larga duración y producciones editoriales." },
        { title: "Escenografía a medida", desc: "Creaciones escenográficas únicas para activaciones de marca, sesiones fotográficas y experiencias inmersivas." },
        { title: "Evento e instalación", desc: "Entrega, montaje y ejecución in situ con una atención meticulosa a cada detalle." },
        { title: "Dirección artística", desc: "Visión artística, estilismo y composición espacial para elevar su proyecto más allá de lo esperado." },
      ],
    },
    projects: {
      label: "Proyectos",
      title: "Proyectos que respiran",
      items: [
        { title: "UTOPAI Studios", cat: "Vegetalización de stand" },
        { title: "MADD Entertainment", cat: "Composiciones florales de stand" },
        { title: "Kérastase · L'Oréal", cat: "Vegetalización floral de stand" },
        { title: "Gala de la Cruz Roja monegasca", cat: "Escenografía floral para eventos" },
        { title: "Netflix Fan Club", cat: "Escenografía floral y vegetal inmersiva" },
        { title: "INTERPARFUMS\u00a0", cat: "Escenografía vegetal" },
        { title: "Sea Breeze", cat: "Espacio corporativo" },
        { title: "Lions Terrace", cat: "Escenografía vegetal de terraza" },
        { title: "DIOR", cat: "Escenografía vegetal" },
      ],
    },
    process: {
      label: "El recorrido Visionature",
      title: "De la inspiración a la instalación",
      intro: "Cuatro movimientos, un mismo gesto continuo: un recorrido acompañado, desde la primera chispa de una idea hasta el último aliento de un decorado terminado.",
      steps: [
        { num: "01", chapter: "Inspiración", title: "Explorar", desc: "Descubrir atmósferas, colecciones y posibilidades. Cada proyecto comienza con una conversación pausada sobre la intención, el lugar y la emoción buscada." },
        { num: "02", chapter: "Composición", title: "Construir", desc: "Seleccionar plantas, composiciones florales, estructuras y elementos decorativos: reunir no una lista de productos, sino una atmósfera coherente." },
        { num: "03", chapter: "Dirección", title: "Diseñar", desc: "Dirección artística, escenografía y afinado. Las ideas se convierten en una visión precisa y ejecutable, sostenida al más alto nivel de exigencia." },
        { num: "04", chapter: "Ejecución", title: "Instalar", desc: "Producción, logística, instalación y puesta en escena final. Una llegada impecable, calibrada in situ hasta que cada detalle respira con justeza." },
      ],
    },
    why: {
      label: "Por qué Visionature",
      title: "No instalamos plantas.",
      titleAccent: "Diseñamos entornos que se convierten en experiencias.",
      body: "Visionature es un estudio de diseño botánico, un socio escenográfico para los lugares que rechazan lo ordinario. Cada encargo comienza con una conversación sobre la atmósfera y termina en un decorado que guarda memoria.",
      stats: [
        { value: "120+", label: "Referencias seleccionadas" },
        { value: "100%", label: "Creaciones a medida" },
        { value: "FR · EU", label: "Ejecución in situ" },
      ],
      points: [
        { num: "01", title: "Único por naturaleza", desc: "Cada proyecto se compone desde cero, sin plantillas ni repeticiones. Cada entorno responde a un lugar, un público y una intención singulares." },
        { num: "02", title: "Una colección curada", desc: "Más de 120 referencias cuidadosamente seleccionadas —plantas, creaciones florales, contenedores y estructuras— reunidas para componer atmósferas coherentes y habitadas." },
        { num: "03", title: "Dominio completo", desc: "Del concepto a la instalación: dirección artística, producción, logística y ejecución. Un equipo, una visión, sostenida de principio a fin." },
        { num: "04", title: "Pensado para dejar huella", desc: "Concebido para ser vivido, fotografiado, compartido y recordado. El objetivo nunca es la decoración. El objetivo es la presencia." },
      ],
    },
    map: {
      legend: { installation: "Instalación", client: "Cliente", hub: "Sede" },
      title: "Dónde intervenimos",
      stats: [
        { number: "+1200", title: "Proyectos realizados", description: "Instalaciones botánicas entregadas en Europa y Oriente Medio." },
        { number: "+30", title: "Países cubiertos", description: "Una red creciente de experiencias botánicas de alta gama en el mundo." },
        { number: "+95", title: "Clientes acompañados", description: "Marcas de lujo, eventos y encargos privados confían en nuestro oficio." },
      ],
      info: [
        { title: "Alcance internacional", description: "De la Costa Azul a Dubái, llevamos la escenografía viva a los lugares más prestigiosos." },
        { title: "Instalaciones móviles", description: "Nuestro equipo se desplaza con toda la capacidad de producción: entrega, montaje y estilismo in situ." },
        { title: "Proyectos a medida", description: "Cada proyecto se diseña desde cero. Sin plantillas ni atajos: solo arte botánico a medida." },
      ],
    },
    contact: {
      label: "Contacto",
      title: "Creemos juntos algo extraordinario",
      subtitle: "Cuéntenos su visión. Crearemos una experiencia botánica a medida.",
      thankYou: "Gracias.",
      thankYouSub: "Hemos recibido su solicitud de proyecto. Nuestro equipo la revisará y se pondrá en contacto con usted en breve.",
      step1Title: "Sobre usted",
      step2Title: "Su proyecto",
      step3Title: "Últimos detalles",
      next: "Continuar",
      back: "Volver",
      cta: "Iniciar su proyecto",
      fullName: "Nombre completo",
      email: "Correo electrónico",
      phone: "Teléfono",
      company: "Nombre de la empresa",
      clientType: "Tipo de cliente",
      clientTypes: [
        { value: "brand", label: "Marca / Empresa" },
        { value: "agency", label: "Agencia" },
        { value: "creator", label: "Creador de contenido / Influencer" },
        { value: "private", label: "Cliente particular (boda / evento)" },
        { value: "other", label: "Otro" },
      ],
      projectType: "Tipo de proyecto",
      projectTypes: [
        { value: "event", label: "Evento (marca / privado)" },
        { value: "photoshoot", label: "Sesión fotográfica / Creación de contenido" },
        { value: "activation", label: "Activación de marca" },
        { value: "installation", label: "Instalación / Decoración" },
        { value: "other", label: "Otro" },
      ],
      location: "Lugar del proyecto",
      locationPlaceholder: "Ciudad, país",
      setting: "Entorno",
      indoor: "Interior",
      outdoor: "Exterior",
      both: "Ambos",
      scale: "Número estimado de invitados",
      scalePlaceholder: "ej. 150 invitados",
      dates: "Fechas del proyecto",
      datesPlaceholder: "Seleccionar las fechas",
      startDate: "Fecha de inicio",
      endDate: "Fecha de fin",
      multiEvents: "Este proyecto incluye varias fechas de evento",
      additionalDates: "Fechas adicionales",
      additionalDatesPlaceholder: "Indique las demás fechas o periodos relevantes para su proyecto.",
      addRange: "+ Añadir otro periodo",
      removeRange: "Eliminar",
      budgetRange: "Presupuesto estimado",
      budgets: [
        { value: "1-5k", label: "1 000€ – 5 000€" },
        { value: "5-10k", label: "5 000€ – 10 000€" },
        { value: "10-20k", label: "10 000€ – 20 000€" },
        { value: "20-35k", label: "20 000€ – 35 000€" },
        { value: "35-50k", label: "35 000€ – 50 000€" },
        { value: "50k+", label: "50 000€+" },
        { value: "tbd", label: "Por definir" },
      ],
      message: "Descripción del proyecto",
      messagePlaceholder: "Cuéntenos su visión, la atmósfera deseada, sus referencias y sus expectativas.",
      stepOf: "de",
    },
    footer: {
      description: "Estudio de escenografía botánica de alta gama. Diseñamos espacios vivos a través de la naturaleza.",
      navigation: "Navegación",
      contactLabel: "Contacto",
      rights: "Todos los derechos reservados.",
    },
  },
} as const;

export type Translations = (typeof translations)["en"];
